import React from "react";
import { Link, useParams } from "react-router-dom";
import { MyConst } from "data/MyConst";
import logoPdf from "assets/logo_pdf.png";
import "./LiveScore.css";

const medalOrder = {
  emas: 1,
  perak: 2,
  perunggu: 3,
};

function publicApiBase() {
  return String(MyConst.URLAPI || "").replace(/\/v1\/?$/, "").replace(/\/$/, "");
}

function assetUrl(location, filename) {
  if (!filename) return "";
  return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/${location}/${filename}`;
}

function formatNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return value || "-";
  return number.toLocaleString("id-ID");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function normalizePayload(payload) {
  return payload?.data || {};
}

function medalClass(name) {
  const key = String(name || "").toLowerCase();
  if (key.includes("emas")) return "gold";
  if (key.includes("perak")) return "silver";
  if (key.includes("perunggu")) return "bronze";
  return "total";
}

function MedalDot({ type }) {
  return <span className={`live-medal-dot ${medalClass(type)}`} />;
}

function AnimatedNumber({ value, animateKey }) {
  const numericValue = Number(value || 0);
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    if (!Number.isFinite(numericValue)) {
      setDisplayValue(value);
      return undefined;
    }
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion) {
      setDisplayValue(numericValue);
      return undefined;
    }

    let frameId = 0;
    let startTime = 0;
    const duration = 850;

    const tick = (time) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(numericValue * eased));
      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    setDisplayValue(0);
    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [numericValue, value, animateKey]);

  return formatNumber(displayValue);
}

function StatCard({ medal, label, value, animateKey }) {
  return (
    <div className="live-score-card">
      <div className="live-score-card-label">
        {medal ? <MedalDot type={medal} /> : null}
        {label}
      </div>
      <div className="live-score-card-value"><AnimatedNumber value={value} animateKey={animateKey} /></div>
    </div>
  );
}

function PublicScoreHeader({ title }) {
  return (
    <header className="live-public-header">
      <div className="live-public-title">{title || "Live Score"}</div>
    </header>
  );
}

function PublicScoreFooter({ kejuaraan }) {
  const ownerName = kejuaraan?.nama_organisasi || MyConst.SYS_OWNER_NAME || "KONI";
  const ownerLogo = kejuaraan?.logo_organisasi ? assetUrl("organisasi-logo", kejuaraan.logo_organisasi) : logoPdf;

  return (
    <footer className="live-public-footer">
      <div className="live-footer-owner">
        <img
          src={ownerLogo}
          alt={ownerName}
          className="live-footer-logo"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = logoPdf;
          }}
        />
        <span>{ownerName}</span>
      </div>
      <div className="live-footer-note">Update sementara versi SIMAKOR</div>
    </footer>
  );
}

function KlasemenTable({ rows, totals, startIndex = 0 }) {
  return (
    <div className="live-score-board">
      <table className="live-score-table compact">
        <thead>
          <tr>
            <th className="center narrow">#</th>
            <th>Kontingen</th>
            <th className="center medal-count">Emas</th>
            <th className="center medal-count">Perak</th>
            <th className="center medal-count">Perunggu</th>
            <th className="center medal-count">Total</th>
            <th className="center action">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => (
            <tr key={`${item.id_kontingen}-${startIndex}-${index}`}>
              <td className="center">{item.rangking || startIndex + index + 1}</td>
              <td>
                <strong className="live-kode">{item.kode_kontingen}</strong>
                <span>{item.nama_kontingen}</span>
              </td>
              <td className="center">{formatNumber(item.emas)}</td>
              <td className="center">{formatNumber(item.perak)}</td>
              <td className="center">{formatNumber(item.perunggu)}</td>
              <td className="center total">{formatNumber(item.total)}</td>
              <td className="center">
                <Link
                  className="live-detail-link"
                  to={`/live_score/${encodeURIComponent(item.id_kejuaraan)}/${encodeURIComponent(item.id_kontingen)}`}
                >
                  Lihat
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function usePublicFetch(path, options = {}) {
  const refreshMs = Number(options.refreshMs || 0);
  const [state, setState] = React.useState({ loading: true, error: "", data: null, version: 0 });

  React.useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function load(isRefresh = false) {
      if (!isRefresh) {
        setState((current) => ({ ...current, loading: true, error: "" }));
      }
      try {
        const response = await fetch(`${publicApiBase()}${path}`, {
          method: "GET",
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || payload?.code >= 400) {
          throw new Error(payload?.message || "Data gagal dimuat");
        }
        if (!ignore) {
          setState((current) => ({
            loading: false,
            error: "",
            data: normalizePayload(payload),
            version: current.version + 1,
          }));
        }
      } catch (error) {
        if (!ignore && error.name !== "AbortError") {
          setState((current) => ({
            loading: false,
            error: isRefresh && current.data ? "" : error.message || "Data gagal dimuat",
            data: isRefresh ? current.data : null,
            version: current.version,
          }));
        }
      }
    }

    load();
    const timer = refreshMs > 0 ? window.setInterval(() => load(true), refreshMs) : null;
    return () => {
      ignore = true;
      if (timer) window.clearInterval(timer);
      controller.abort();
    };
  }, [path, refreshMs]);

  return state;
}

function LiveScoreList() {
  const { idKejuaraan } = useParams();
  const path = idKejuaraan ? `/klasemen/${encodeURIComponent(idKejuaraan)}` : "/klasemen";
  const { loading, error, data, version } = usePublicFetch(path, { refreshMs: 30000 });
  const rows = Array.isArray(data?.data) ? data.data : [];
  const kejuaraan = data?.kejuaraan || {};
  const totals = rows.reduce(
    (acc, item) => ({
      emas: acc.emas + Number(item.emas || 0),
      perak: acc.perak + Number(item.perak || 0),
      perunggu: acc.perunggu + Number(item.perunggu || 0),
      total: acc.total + Number(item.total || 0),
    }),
    { emas: 0, perak: 0, perunggu: 0, total: 0 },
  );
  const splitAt = Math.ceil(rows.length / 2);
  const leftRows = rows.slice(0, splitAt);
  const rightRows = rows.slice(splitAt);
  const hasTwoColumns = rows.length > 12;
  const primaryRows = hasTwoColumns ? leftRows : rows;

  return (
    <main className="live-score-page">
      <PublicScoreHeader title={kejuaraan?.nama_kejuaraan || "Klasemen Kejuaraan"} />
      <section className="live-score-container">
        <div className="live-watermark" aria-hidden="true">
          <img src={logoPdf} alt="" />
        </div>

        {loading ? <div className="live-score-info">Memuat klasemen...</div> : null}
        {error ? <div className="live-score-error">{error}</div> : null}

        {!loading && !error ? (
          <>
            <div className={`live-score-grid ${hasTwoColumns ? "two" : "one"}`}>
              <StatCard medal="Emas" label="Emas" value={totals.emas} animateKey={version} />
              <StatCard medal="Perak" label="Perak" value={totals.perak} animateKey={version} />
              <StatCard medal="Perunggu" label="Perunggu" value={totals.perunggu} animateKey={version} />
              <StatCard medal="Total" label="Total" value={totals.total} animateKey={version} />
            </div>

            {rows.length > 0 ? (
              <div className={`live-klasemen-panels ${hasTwoColumns ? "two" : "one"}`}>
                <KlasemenTable rows={primaryRows} totals={totals} />
                {hasTwoColumns && rightRows.length > 0 ? <KlasemenTable rows={rightRows} totals={totals} startIndex={splitAt} /> : null}
              </div>
            ) : (
              <div className="center live-empty">Data klasemen belum tersedia.</div>
            )}
          </>
        ) : null}
      </section>
      <PublicScoreFooter kejuaraan={kejuaraan} />
    </main>
  );
}

function LiveScoreDetail() {
  const { idKejuaraan, idKontingen } = useParams();
  const [keyword, setKeyword] = React.useState("");
  const [page, setPage] = React.useState(1);
  const perPage = 20;
  const { loading, error, data } = usePublicFetch(
    `/detil_klasemen/${encodeURIComponent(idKejuaraan)}/${encodeURIComponent(idKontingen)}`,
  );
  const { data: klasemenData } = usePublicFetch(`/klasemen/${encodeURIComponent(idKejuaraan)}`);
  const rows = Array.isArray(data?.data) ? data.data : [];
  const klasemenRows = Array.isArray(klasemenData?.data) ? klasemenData.data : [];
  const kejuaraan = data?.kejuaraan || {};
  const kontingen = klasemenRows.find((item) => String(item.id_kontingen) === String(idKontingen)) || rows[0] || {};
  const summary = {
    emas: kontingen.emas || 0,
    perak: kontingen.perak || 0,
    perunggu: kontingen.perunggu || 0,
    total: kontingen.total || 0,
  };
  const filteredRows = rows
    .slice()
    .sort((a, b) => {
      const medalDiff = (medalOrder[String(a.medali || "").toLowerCase()] || 99) - (medalOrder[String(b.medali || "").toLowerCase()] || 99);
      if (medalDiff !== 0) return medalDiff;
      return String(a.cabor || "").localeCompare(String(b.cabor || ""));
    })
    .filter((item) => {
      const text = [
        item.medali,
        item.atlet_peraih,
        item.cabor,
        item.nomor_pertandingan,
        item.catatan,
      ].join(" ").toLowerCase();
      return text.includes(keyword.trim().toLowerCase());
    });
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((safePage - 1) * perPage, safePage * perPage);

  React.useEffect(() => {
    setPage(1);
  }, [keyword]);

  return (
    <main className="live-score-page">
      <PublicScoreHeader title={kejuaraan?.nama_kejuaraan || "Detail Klasemen"} />
      <section className="live-score-container">
        <header className="live-score-header detail">
          <h2>Kontingen {kontingen?.nama_kontingen || "-"}</h2>
        </header>

        {loading ? <div className="live-score-info">Memuat detail klasemen...</div> : null}
        {error ? <div className="live-score-error">{error}</div> : null}

        {!loading && !error ? (
          <>
            <div className="live-score-grid detail">
              <StatCard label="Ranking" value={kontingen?.rangking || "-"} />
              <StatCard medal="Emas" label="Emas" value={summary.emas} />
              <StatCard medal="Perak" label="Perak" value={summary.perak} />
              <StatCard medal="Perunggu" label="Perunggu" value={summary.perunggu} />
              <StatCard medal="Total" label="Total" value={summary.total} />
            </div>

            <div className="live-detail-toolbar">
              <input
                className="live-search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="Cari nama atlet, cabor, atau nomor pertandingan"
              />
              <Link className="live-back-link" to={idKejuaraan ? `/live_score/${encodeURIComponent(idKejuaraan)}` : "/live_score"}>
                <i className="fas fa-arrow-left" aria-hidden="true" />
                <span className="sr-only">Kembali ke klasemen</span>
              </Link>
            </div>

            <div className="live-score-table-wrap">
              <table className="live-score-table detail">
                <thead>
                  <tr>
                    <th className="center narrow">No.</th>
                    <th>Medali</th>
                    <th>Atlet Peraih</th>
                    <th>Cabor</th>
                    <th>Nomor Pertandingan</th>
                    <th>Catatan</th>
                    <th>Waktu</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((item, index) => (
                    <tr key={`${item.medali}-${item.cabor}-${item.nomor_pertandingan}-${index}`}>
                      <td className="center">{(safePage - 1) * perPage + index + 1}</td>
                      <td><MedalDot type={item.medali} />{item.medali || "-"}</td>
                      <td>{item.atlet_peraih || "-"}</td>
                      <td>{item.cabor || "-"}</td>
                      <td>{item.nomor_pertandingan || "-"}</td>
                      <td>{item.catatan || "-"}</td>
                      <td>{formatDateTime(item.waktu_pencatatan)}</td>
                    </tr>
                  ))}
                  {pagedRows.length < 1 ? (
                    <tr>
                      <td colSpan={7} className="center live-empty">Detail medali belum tersedia.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {filteredRows.length > perPage ? (
              <div className="live-pagination">
                <div>Jumlah Data: {formatNumber(filteredRows.length)}</div>
                <div className="live-pagination-actions">
                  <button type="button" disabled={safePage <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                    Sebelumnya
                  </button>
                  <span>Halaman {safePage} / {totalPages}</span>
                  <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                    Berikutnya
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
      <PublicScoreFooter kejuaraan={kejuaraan} />
    </main>
  );
}

export default function LiveScore() {
  const { idKejuaraan, idKontingen } = useParams();
  if (idKejuaraan && idKontingen) {
    return <LiveScoreDetail />;
  }
  return <LiveScoreList />;
}
